import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import CircularProgress from "@mui/material/CircularProgress";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Dialog, Form } from "../index";
import styles from "./wizard.module.scss";
const NEXT_STEP_LABEL = "Next step";
const PREVIOUS_STEP_LABEL = "Previous";
const getNextButtonLabel = (step, isLastStep, isForm) => {
    var _a;
    if ((_a = step.nextStep) === null || _a === void 0 ? void 0 : _a.label) {
        return step.nextStep.label;
    }
    if (isLastStep) {
        return isForm ? "Submit" : "Close";
    }
    if (step.isOptional) {
        return "Skip this step";
    }
    return NEXT_STEP_LABEL;
};
const WizardContent = ({ title, subtitle, steps, activeStep, dialogContentClasses, onStepChange, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const activeStepConfig = steps[activeStep];
    const formMethods = useForm({
        values: activeStepConfig.formValues,
        resolver: yupResolver(activeStepConfig.schema),
        mode: (_a = activeStepConfig.formMode) !== null && _a !== void 0 ? _a : "onSubmit",
    });
    const disabledSteps = steps.reduce((result, currentStep, currentIndex) => {
        var _a;
        if (((_a = currentStep.previousStep) === null || _a === void 0 ? void 0 : _a.isHidden) && activeStep >= currentIndex) {
            return Array.from({ length: currentIndex }, (_value, index) => index);
        }
        else {
            return result;
        }
    }, []);
    const shouldUseForm = Boolean(activeStepConfig.formValues) && Boolean(activeStepConfig.schema);
    const showPreviousButton = activeStep > 0 && !((_b = activeStepConfig.previousStep) === null || _b === void 0 ? void 0 : _b.isHidden);
    const isLastStep = activeStep === steps.length - 1;
    const previousIsLoading = (_d = (_c = activeStepConfig.previousStep) === null || _c === void 0 ? void 0 : _c.isLoading) !== null && _d !== void 0 ? _d : false;
    const nextIsLoading = ((_f = (_e = activeStepConfig.nextStep) === null || _e === void 0 ? void 0 : _e.isLoading) !== null && _f !== void 0 ? _f : false) ||
        formMethods.formState.isSubmitting;
    const startIcon = (React.createElement(CircularProgress, { size: "16px", thickness: 6, className: styles["progress-icon"] }));
    const stepBody = (React.createElement(React.Fragment, null,
        React.createElement(Dialog.Body, { className: dialogContentClasses }, activeStepConfig.component),
        React.createElement(Dialog.Footer, null,
            React.createElement(Dialog.Footer.Meta, null,
                React.createElement(Dialog.StepCounter, { current: activeStep + 1, total: steps.length })),
            React.createElement(Dialog.Footer.Actions, null,
                showPreviousButton && (React.createElement(Button, { color: "secondary", onClick: () => {
                        var _a;
                        return (_a = activeStepConfig.onPrevious) === null || _a === void 0 ? void 0 : _a.call(activeStepConfig, shouldUseForm ? formMethods.getValues() : undefined);
                    }, disabled: previousIsLoading, startIcon: previousIsLoading && startIcon }, (_h = (_g = activeStepConfig.previousStep) === null || _g === void 0 ? void 0 : _g.label) !== null && _h !== void 0 ? _h : PREVIOUS_STEP_LABEL)),
                React.createElement(Button, { type: shouldUseForm ? "submit" : "button", color: activeStepConfig.isOptional ? "secondary" : "primary", onClick: shouldUseForm ? undefined : () => activeStepConfig.onNext(), disabled: nextIsLoading, startIcon: nextIsLoading && startIcon }, getNextButtonLabel(activeStepConfig, isLastStep, shouldUseForm))))));
    return (React.createElement(React.Fragment, null,
        React.createElement(Dialog.Header, null,
            React.createElement(Dialog.Header.Title, null, title),
            subtitle && (React.createElement(Dialog.Header.Subtitle, null, subtitle))),
        React.createElement(Dialog.Stepper, { steps: steps.map((step, stepIndex) => ({
                label: step.label,
                isDisabled: disabledSteps.includes(stepIndex) || stepIndex > activeStep,
            })), activeStep: activeStep, onStepChange: onStepChange }),
        shouldUseForm ? (React.createElement(FormProvider, { ...formMethods },
            React.createElement(Form.Form, { className: styles.form, onSubmit: formMethods.handleSubmit(activeStepConfig.onNext), ...activeStepConfig.formProps }, stepBody))) : (stepBody)));
};
/** The wizard bands with full step navigation, for a `Dialog.Frame` body. See {@link WizardProps} for prop details. */
const Wizard = (props) => (React.createElement(React.Fragment, null,
    React.createElement(WizardContent, { key: props.activeStep, ...props })));
export default Wizard;
//# sourceMappingURL=Wizard.js.map